// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable, TemplateRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Breadcrumb } from '@interfaces/nav.interface';
import { UserService } from './user.service';
import { AccountType, OEM, UserTypes } from '@interfaces/user.interfaces';
import { NavItem, RouteType } from '@interfaces/interfaces';
import { sideNavMenu, userMenu } from '../utils/constants';
import { DealershipService } from './dealership.service';
import { User } from '@sentry/angular-ivy';
import * as _ from 'lodash';
import { FeatureFlagService } from './feature-flag.service';

@Injectable({
    providedIn: 'root',
})
export class NavService {
    public accountType: AccountType = null;
    public userType: AccountType = null;
    private _breadcrumbs$: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);
    private _pageActions: BehaviorSubject<TemplateRef<any>> = new BehaviorSubject<TemplateRef<any>>(null);
    private _helpPanel: BehaviorSubject<TemplateRef<any>> = new BehaviorSubject<TemplateRef<any>>(null);
    public pageTitle: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public headerTooltip: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public sideNavMenu = new BehaviorSubject<NavItem[]>(sideNavMenu);
    public userMenu = new BehaviorSubject<NavItem[]>(userMenu);
    private navState = new BehaviorSubject<string>(localStorage.getItem('nav-state') || 'expanded');
    public navState$ = this.navState.asObservable();

    constructor(
        private readonly dealershipService: DealershipService,
        private readonly title: Title,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly featureFlagService: FeatureFlagService
    ) {}

    private addBreadcrumb(routeSnapshot: ActivatedRouteSnapshot, breadcrumbs: Breadcrumb[]): Breadcrumb[] {
        if (!routeSnapshot.firstChild) {
            return breadcrumbs;
        }

        // Needed for a few calculations
        const oemCode = this.dealershipService.dealership?.oemCode;

        this.headerTooltip.next(routeSnapshot.firstChild.data.headerTooltip);

        // if it already exists, dont' add it;
        //use regex instead of includes for strict comparsions (deals vs deal)
        const exists = breadcrumbs.find((b) => new RegExp(`\\b${b.label}\\b`).test(routeSnapshot.firstChild.data.title));
        const isCustomer = this.userService.isCustomer;
        if (routeSnapshot.firstChild.data.title && !exists) {
            breadcrumbs.push({
                url: isCustomer ? this.setUrl(breadcrumbs, routeSnapshot) : this.setDealerUrl(breadcrumbs, routeSnapshot),
                label: routeSnapshot.firstChild.data.title,
            });

            // set the title of the page to be E-Shop Leads if navigating to the leads page
            if (routeSnapshot.firstChild.routeConfig.path === 'leads') {
                this.handleLeadsTitle(oemCode);
            } else {
                this.pageTitle.next(routeSnapshot.firstChild.data.title);
            }
        }

        if (routeSnapshot.firstChild.routeConfig.path === 'gettingstarted') {
            this.setTitle('Getting Started');
        } else if (routeSnapshot.firstChild.routeConfig.path === 'storage') {
            this.setTitle('Document Storage');
        } else if (routeSnapshot.firstChild.routeConfig.path === 'document-library-admin') {
            this.setTitle('Document Library');
        }
        //Customer
        else if (isCustomer && routeSnapshot.firstChild.routeConfig.path === 'help') {
            this.setTitle('Help');
        } else if (isCustomer && routeSnapshot.firstChild.routeConfig.path === 'settings') {
            this.setTitle('Settings');
        }
        //End Customer

        return this.addBreadcrumb(routeSnapshot.firstChild, breadcrumbs);
    }

    setNavState(state: 'expanded' | 'collapsed'): void {
        localStorage.setItem('nav-state', state);
        this.navState.next(state);
    }

    get pageActions$() {
        return this._pageActions.asObservable();
    }

    setPageActions(actions: TemplateRef<any> | null) {
        this._pageActions.next(actions);
    }

    get helpPanel$() {
        return this._helpPanel.asObservable();
    }

    setHelpPanel<T>(content: TemplateRef<T> | null) {
        this._helpPanel.next(content);
    }

    getBreadCrumbs$() {
        return this._breadcrumbs$.asObservable();
    }

    setBreadCrumbs$(breadcrumbs: Breadcrumb[]) {
        //this function allows us to overwrite standard breadcrumbs in situations where desired breadcrumbs cannot be derived from URL
        this._breadcrumbs$.next(breadcrumbs);
    }

    getBreadCrumbLength() {
        return this._breadcrumbs$.value.length;
    }
    //todo: try moving this into the permission service
    isAllowedToSeeDocumentLibraryAdmin = (navItem: NavItem, userType: UserTypes) => {
        return !(navItem.type === RouteType.DocumentLibraryAdmin && [UserTypes.Dealer, UserTypes.Customer].includes(userType));
    };

    //todo: try moving this into the permission service
    userDoesNotHaveAccessToResource = (navItem: NavItem) => {
        const missingPermission =
            navItem?.permissions?.permissionIds &&
            !navItem?.permissions?.permissionIds?.some((id) =>
                this.userService.user?.dealerAppPermissions.find((d) => d.dealerId === this.userService.user?.dealerId)?.userPermissionId?.includes(id)
            );

        return missingPermission;
    };

    markResourcesWithoutAccessAsDisabled = (menu: NavItem[]) => {
        return menu.map((navItem) => ({
            ...navItem,
            disabled: this.userDoesNotHaveAccessToResource(navItem),
        }));
    };

    sendToHome() {
        this.router.navigate(['/dashboard', 'leads']);
    }

    public getSideNavMenu(): Observable<NavItem[]> {
        const userMenu = this.userMenu.getValue();
        let menu = this.sideNavMenu.getValue();
        const user = this.userService.user;

        // remove dealer & user management from side nav since they are user profile type
        menu = menu
            .concat(userMenu)
            .filter(
                (navItem) =>
                    navItem.accountTypes.includes(this.accountType) &&
                    navItem.type !== RouteType.DealerManagement &&
                    navItem.type !== RouteType.UserManagement &&
                    navItem.type !== RouteType.Dashboard
            )
            .filter((navItem) => this.isAllowedToSeeDocumentLibraryAdmin(navItem, user.userType));

        menu.forEach((item) => {
            item.disabled = this.userDoesNotHaveAccessToResource(item) || false;
        });

        menu = _.sortBy(menu, 'sortOrder');

        if (
            this.accountType === AccountType.stellantisDealer ||
            (this.dealershipService.dealershipInfo.oemCode === OEM.stellantis &&
                this.accountType !== AccountType.ddjBuyer &&
                this.accountType !== AccountType.stellantisBuyer &&
                menu.length > 0)
        ) {
            menu[0].title = 'E-SHOP Leads';
        }

        return of(menu);
    }

    // if window is smaller than 1200px side nav and user nav are in hamburger menu
    // if window is greater than 1200px side nav is visible
    public getUserMenu(user: User): Observable<NavItem[]> {
        let menu = [];
        const userMenuBasedOnPermission = userMenu;

        //In the furture we will probably need a way to get the deal dealerId for a customer instead of -1.
        this.featureFlagService.getFeatures(user.dealerId).subscribe((res) => {
            if (res?.data) {
                this.featureFlagService.setFeatures(res.data);
            }
        });

        const buildMobileMenu = () => {
            this.getSideNavMenu().subscribe((m) => {
                menu = _.uniqBy(
                    m.concat(userMenuBasedOnPermission).filter((navItem) => navItem.accountTypes.includes(this.accountType)),
                    'title'
                );
            });
        };

        const buildDesktopUserDropdownMenu = () => {
            menu = this.markResourcesWithoutAccessAsDisabled(userMenuBasedOnPermission.filter((navItem) => navItem.accountTypes.includes(this.accountType)));
        };

        if (window.innerWidth < 1200) {
            buildMobileMenu();
        } else {
            buildDesktopUserDropdownMenu();
        }

        if (user.userType !== UserTypes.Customer) {
            menu = this.markResourcesWithoutAccessAsDisabled(menu);
            menu = _.sortBy(menu, 'sortOrder');
        }

        return of(menu);
    }

    setRouteState(routeSnapshot: ActivatedRouteSnapshot) {
        this._breadcrumbs$.next(this.addBreadcrumb(routeSnapshot, []));
    }

    // when selecting a nav item from the side menu
    // don't need for user menu since you can't see
    public setSelectedRoute(prefix: string, item: NavItem): void {
        const routeParts = item.route.split('/');
        this.router.navigate([prefix, ...routeParts]).then(() => {
            const activeRoute = this.router.url.includes(item.route);
            let foundFirst = false;
            sideNavMenu
                .concat(userMenu)
                .filter((navItem) => navItem.accountTypes.includes(this.accountType))
                .map((i) => {
                    // In case a guard blocks a navigation we don't update active
                    if (activeRoute) {
                        // only if selected item.route matches a route in the list will we set it to active
                        if (item.route === i.route) {
                            const couldBeActive = this.router.url.includes(item.route) ? true : false;
                            i.isActive = !foundFirst && couldBeActive;
                            foundFirst = foundFirst || couldBeActive;
                        }
                    }
                });
        });
    }

    // determine which nav item is highlighted on page refresh, also for child routes
    public setSelectedRouteByURL(url: string): void {
        let firstFound = false;
        sideNavMenu
            .concat(userMenu)
            .filter((navItem) => navItem.accountTypes.includes(this.accountType))
            .map((i) => {
                // get rid of '' & dashboard - we don't route there
                // if the url is vehicles we are on the buyer page and
                // we need to set Vehicles as the selected nav item
                const couldBeActive = url.includes(i.route) || (i.route.includes('deals') && url === 'vehicles');
                // only one route in the nav should be selected at a time
                i.isActive = !firstFound && couldBeActive;
                firstFound = firstFound || couldBeActive;
            });
    }

    setTitle(title: string) {
        return this.title.setTitle(`${title} - EZ DOCS`);
    }

    private setDealerUrl(breadcrumbs, routeSnapshot): any {
        if (routeSnapshot.firstChild.url.length >= 1) {
            if (breadcrumbs.length > 0) {
                const previousSegment = breadcrumbs[breadcrumbs.length - 1].url;
                return `${previousSegment}/${routeSnapshot.firstChild.url[0].path}`;
            } else {
                return routeSnapshot.firstChild.url[0].path;
            }
        }
    }

    public setLeadsNavItemTitle(oemCode: string): void {
        const sideNav = this.sideNavMenu.getValue();

        // change leads to eshop if OEM is stellantis and the accountType is dealer
        oemCode === OEM.stellantis && (this.accountType === AccountType.ddjDealer || this.accountType === AccountType.stellantisDealer)
            ? (sideNav[0].title = 'E-SHOP Leads')
            : (sideNav[0].title = 'Leads');
    }

    private setUrl(breadcrumbs, routeSnapshot): any {
        if (routeSnapshot.firstChild.data.title === 'Vehicles') {
            return 'vehicles';
        } else {
            return breadcrumbs.length === 1
                ? breadcrumbs[breadcrumbs.length - 1]?.url + '/' + routeSnapshot.firstChild._urlSegment.segments[2]
                : breadcrumbs[breadcrumbs.length - 1]?.url + '/' + routeSnapshot.firstChild._urlSegment.segments[3];
        }
    }

    public handleLeadsTitle(oemCode: string) {
        oemCode === OEM.stellantis && (this.accountType === AccountType.ddjDealer || this.accountType === AccountType.stellantisDealer)
            ? this.pageTitle.next('E-SHOP Leads')
            : this.pageTitle.next('Leads');
    }
}
